import objectID from 'bson-objectid';
import {
  SKY_TRANSFORMS_SOURCE,
  SKY_TRANSFORMS_FILENAME
} from '../../../utils/skyTransformsSource';
import {
  SKY_DRAW_SOURCE,
  SKY_DRAW_FILENAME
} from '../../../utils/skyDrawSource';
import {
  AR_SLIDER_SOURCE,
  AR_SLIDER_FILENAME
} from '../../../utils/arSliderSource';
import { ensureScriptTag } from '../../../utils/constellationInject';
import { lilGuiURL } from '../../../../common/p5URLs';
import { getHTMLFile } from '../reducers/files';
import { createFile, updateFileContent, ensureFolder } from './files';
import { setUnsavedChanges } from './ide';

/** Injected helper files live under this folder in the sketch's file tree. */
export const HELPERS_FOLDER = 'helpers';

/**
 * The sky-helper files the "Add Helpers…" menu can drop into a sketch, keyed by the value the
 * menu dispatches. Each is a standalone script eval'd verbatim in the editor preview and in AR:
 *   - transforms → skyTransforms.js: celestial math + frame ops (applyGeoLock, lockStarsToSky)
 *   - draw       → skyDraw.js: drawing + constellation processing (createStarGeometry, drawStars,
 *                  drawConstellationLines). Carries its own raDecToVec, so it needs nothing else.
 *   - arSlider   → arSlider.js: createArSlider()/resetArSliders() registry synced to AR.
 */
export const HELPER_FILES = {
  transforms: { name: SKY_TRANSFORMS_FILENAME, source: SKY_TRANSFORMS_SOURCE },
  draw: { name: SKY_DRAW_FILENAME, source: SKY_DRAW_SOURCE },
  arSlider: { name: AR_SLIDER_FILENAME, source: AR_SLIDER_SOURCE }
};

/** A ProjectFile node with generated content. */
function makeFile(name, content) {
  const id = objectID().toHexString();
  return { name, id, _id: id, content, children: [] };
}

/**
 * Add one sky-helper file to the current sketch: create it + wire its `index.html` <script> tag,
 * unless it is already present (checked by filename). A no-op when the file and its tag both
 * already exist, so it is safe to invoke repeatedly.
 *
 * @param {'transforms'|'draw'|'arSlider'} kind which helper file to add
 */
export function addHelperFile(kind) {
  return (dispatch, getState) => {
    const helper = HELPER_FILES[kind];
    if (!helper) return;

    const state = getState();
    const htmlFile = getHTMLFile(state.files);
    const existing = state.files.find(
      (f) => f && f.fileType === 'file' && f.name === helper.name
    );

    let changed = false;
    if (!existing) {
      const folderId = dispatch(ensureFolder(HELPERS_FOLDER));
      dispatch(createFile(makeFile(helper.name, helper.source), folderId));
      changed = true;
    }
    if (htmlFile) {
      let html = ensureScriptTag(
        htmlFile.content,
        `${HELPERS_FOLDER}/${helper.name}`
      );
      // The arSlider helper renders through lil-gui; make the library available by
      // default so sliders "just work" — loaded at page load (before sketch.js),
      // which sidesteps any async timing gap. Same tag the Manage Libraries toggle
      // adds, so useP5Version reflects it as enabled; enableLilGui() can still
      // (re)load it from code if the tag is later removed.
      if (kind === 'arSlider') {
        html = ensureScriptTag(html, lilGuiURL);
      }
      if (html !== htmlFile.content) {
        dispatch(updateFileContent(htmlFile.id, html));
        changed = true;
      }
    }
    if (changed) dispatch(setUnsavedChanges(true));
  };
}

export default addHelperFile;
